import type { Municipality } from '@/graphql/types/municipality';
import type { Variable } from '@/graphql/types/variables';
import type { TranslationKey } from '@/hooks/useTranslation';
import { Classification } from '@/types/classification';

interface KpisDataProps {
  variables: Variable[];
  municipality?: Municipality;
  t: (key: TranslationKey) => string;
}

export interface Kpi {
  title: string;
  value: string | number | number[] | string[];
  tooltip?: {
    title: string;
    description: string;
    source: string;
    period: string;
    index: number;
  };
}

interface CreateTooltipProps {
  variableName: string;
  key: string;
  title: string;
  description: string;
  source: string;
  period: string;
  index: number;
}

export const kpisData = ({ variables, t, municipality }: KpisDataProps) => {
  const classificationNormalize = (classification?: Classification) => {
    switch (classification) {
      case Classification.RURAL:
        return t('label.label.rural');
      case Classification.URBAN:
        return t('label.label.urban');
      default:
        return t('label.label.all');
    }
  };

  // Função para criar tooltip apenas se houver dados válidos
  const createTooltip = ({
    variableName,
    key,
    title,
    description,
    source,
    period,
    index,
  }: CreateTooltipProps) => {
    if (variableName.includes(key.trim())) {
      return {
        title,
        description,
        source,
        period,
        index,
      };
    }

    return undefined;
  };

  const kpis: Kpi[] = [];

  const biome = t('page.municipality.municipality.card.biome').split(' ')[0];
  const classification = t('page.municipality.municipality.card.classification').split(' ')[0];
  const population = t('page.municipality.municipality.card.population').split(' ')[0];
  const area = t('page.municipality.municipality.card.area').split(' ')[0];
  const populationDensity = t('page.municipality.municipality.card.population_density').split(
    '('
  )[0];

  // Encontrar a variável correspondente para cada KPI
  const biomeVariable = variables.find((v) => v.name.includes(biome));
  const classificationVariable = variables.find((v) => v.name.includes(classification));
  const populationVariable = variables.find((v) => v.name.includes(population));
  const areaVariable = variables.find((v) => v.name.includes(area));
  const populationDensityVariable = variables.find((v) =>
    v.name.includes(populationDensity.trim())
  );

  // Extract biomes from municipality data
  const biomesData = municipality?.biomes?.length
    ? municipality.biomes.map((biome) => biome.biome)
    : [t('messages.success.message.noData')];

  // Criar KPI para Bioma
  kpis.push({
    title: t('page.municipality.municipality.card.biome'),
    value: biomesData,
    tooltip: createTooltip({
      variableName: biomeVariable?.name || '',
      key: biome,
      title: t('page.municipality.municipality.card.biome'),
      description: biomeVariable?.description || '',
      source: biomeVariable?.source || '',
      period: biomeVariable?.period || '',
      index: 0,
    }),
  });

  // Criar KPI para Classificação
  kpis.push({
    title: t('page.municipality.municipality.card.classification'),
    value: classificationNormalize(municipality?.classification_type) || '',
    tooltip: createTooltip({
      variableName: classificationVariable?.name || '',
      key: classification,
      title: t('page.municipality.municipality.card.classification'),
      description: classificationVariable?.description || '',
      source: classificationVariable?.source || '',
      period: classificationVariable?.period || '',
      index: 1,
    }),
  });

  // Criar KPI para População
  kpis.push({
    title: t('page.municipality.municipality.card.population'),
    value: municipality?.population || 0,
    tooltip: createTooltip({
      variableName: populationVariable?.name || '',
      key: population,
      title: t('page.municipality.municipality.card.population'),
      description: populationVariable?.description || '',
      source: populationVariable?.source || '',
      period: populationVariable?.period || '',
      index: 2,
    }),
  });

  // Criar KPI para Área
  kpis.push({
    title: t('page.municipality.municipality.card.area'),
    value: municipality?.area_km2 || 0,
    tooltip: createTooltip({
      variableName: areaVariable?.name || '',
      key: area,
      title: t('page.municipality.municipality.card.area'),
      description: areaVariable?.description || '',
      source: areaVariable?.source || '',
      period: areaVariable?.period || '',
      index: 3,
    }),
  });

  // Criar KPI para Densidade Populacional
  kpis.push({
    title: t('page.municipality.municipality.card.population_density'),
    value: municipality?.density_demographic || 0,
    tooltip: createTooltip({
      variableName: populationDensityVariable?.name || '',
      key: populationDensity,
      title: t('page.municipality.municipality.card.population_density'),
      description: populationDensityVariable?.description || '',
      source: populationDensityVariable?.source || '',
      period: populationDensityVariable?.period || '',
      index: 4,
    }),
  });

  return kpis;
};
